import React, { useState } from 'react';
import { Check, AlertTriangle, HelpCircle, XCircle, List } from 'lucide-react';
import { Product } from '../types';
import { ENumberWarning } from './ENumberWarning';
import { ChemicalWarning } from './ChemicalWarning';
import { analyzeIngredients } from '../lib/eNumberService';
import { analyzeChemicals } from '../lib/chemicalService';
import { IngredientsModal } from './IngredientsModal';

const StatusIcon = ({ status }: { status: Product['status'] }) => {
  switch (status) {
    case 'halal':
      return <Check className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500" />;
    case 'haram':
      return <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500" />;
    case 'mushbooh':
      return <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-500" />;
    default:
      return <HelpCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500" />;
  }
};

const StatusBadge = ({ status }: { status: Product['status'] }) => {
  const colors = {
    halal: 'bg-emerald-100 text-emerald-800',
    haram: 'bg-red-100 text-red-800',
    mushbooh: 'bg-yellow-100 text-yellow-800 flex items-center gap-1.5',
    unknown: 'bg-gray-100 text-gray-800',
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs sm:text-sm font-medium ${colors[status]}`}>
      {status === 'mushbooh' && <AlertTriangle className="w-3.5 h-3.5" />}
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

interface ProductCardProps {
  product: Product;
  showTimestamp?: boolean;
  timestamp?: Date;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  showTimestamp,
  timestamp,
}) => {
  const [eNumberAnalysis, setENumberAnalysis] = React.useState<Awaited<ReturnType<typeof analyzeIngredients>> | null>(null);
  const [chemicalAnalysis, setChemicalAnalysis] = React.useState<Awaited<ReturnType<typeof analyzeChemicals>> | null>(null);
  const [showIngredients, setShowIngredients] = useState(false);

  React.useEffect(() => {
    if (product.ingredients) {
      analyzeIngredients(product.ingredients)
        .then(setENumberAnalysis)
        .catch(console.error);
      
      analyzeChemicals(product.ingredients)
        .then(setChemicalAnalysis)
        .catch(console.error);
    }
  }, [product.ingredients]);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="flex items-start p-3 sm:p-4 gap-3 sm:gap-4">
        {product.imageUrl && (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-20 h-20 sm:w-24 sm:h-24 object-cover rounded-lg flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
                {product.name}
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 truncate">
                {product.brand}
              </p>
            </div>
            <StatusIcon status={product.status} />
          </div>
          <div className="mt-2 space-y-2">
            <StatusBadge status={product.status} />
            {product.statusReason && (
              <p className={`text-xs sm:text-sm ${
                product.status === 'mushbooh' ? 'text-yellow-700' : 
                product.status === 'haram' ? 'text-red-700' : 
                'text-gray-600'
              }`}>
                {product.status === 'mushbooh' ? 
                  product.statusReason.split('. ')[0] : // Only show first sentence for mushbooh
                  product.statusReason
                }
              </p>
            )}
          </div>
          {product.certifiedBy && (
            <p className="mt-2 text-xs sm:text-sm text-gray-600">
              Certified by: {product.certifiedBy}
            </p>
          )}
          {showTimestamp && timestamp && (
            <p className="mt-1 text-xs text-gray-500">
              Scanned: {timestamp.toLocaleDateString()}
            </p>
          )}
        </div>
      </div>
      
      {eNumberAnalysis && (eNumberAnalysis.haramENumbers.length > 0 || eNumberAnalysis.mushboohENumbers.length > 0) && (
        <div className="border-t border-gray-100 px-3 sm:px-4 py-3">
          <ENumberWarning eNumbers={eNumberAnalysis} />
        </div>
      )}

      {chemicalAnalysis && (chemicalAnalysis.haramChemicals.length > 0 || chemicalAnalysis.mushboohChemicals.length > 0) && (
        <div className="border-t border-gray-100 px-3 sm:px-4 py-3">
          <ChemicalWarning chemicals={chemicalAnalysis} />
        </div>
      )}

      {product.ingredients && product.ingredients.length > 0 && (
        <>
        <div className="border-t border-gray-100 px-3 sm:px-4 py-2 sm:py-3">
          <button
            onClick={() => setShowIngredients(true)}
            className="w-full flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors py-1"
          >
            <List className="w-4 h-4" />
            Show Ingredients
          </button>
        </div>
        <IngredientsModal
          isOpen={showIngredients}
          onClose={() => setShowIngredients(false)}
          ingredients={product.ingredients}
        />
        </>
      )}
    </div>
  );
};